import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import { apiService, getCurrentLocale } from '../services/api';
import { getImageUrl, getBestImageUrl } from '../utils/imageUrl';
import { useGA4 } from '../hooks/useGA4';
import { Menu, ArrowRight, ArrowDown, Whatsapp } from './icons';
import { useBooking } from '../contexts/BookingContext';
import defaultLogo from '../assets/images/Header/logo.svg';

interface NavigationItem {
  label: string;
  url: string;
  isExternal?: boolean;
  subpages?: NavigationItem[];
}

interface CTAButton {
  text: string;
  url: string;
  variant?: 'primary' | 'secondary';
}

interface HeaderSectionData {
  siteName?: string;
  logo?: {
    data?: {
      attributes?: {
        url?: string;
      };
    };
  };
  navigationItems?: NavigationItem[];
  contactPhone?: string;
  contactEmail?: string;
  ctaButton?: CTAButton;
}

function Header(): React.JSX.Element {
  const locale = getCurrentLocale();
  const { data, loading, error } = useApi(apiService.getHeaderSection, [locale]);
  const { data: globalSettings } = useApi(apiService.getGlobalSettings, [locale]);
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSubmenu, setExpandedSubmenu] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { openBookingPopup } = useBooking();
  
  // GA4 tracking
  const { trackMenuClick, trackLanguageSwitch, trackEvent } = useGA4();

  // Handle scroll detection for sticky header
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsAppClick = (): void => {
    // Track WhatsApp click
    trackEvent('whatsapp_click', {
      location: 'header',
      device_type: isMobileMenuOpen ? 'mobile' : 'desktop'
    });
    
    // Handle WhatsApp button click - could open WhatsApp chat or navigate to WhatsApp
    if ((import.meta as any)?.env?.DEV) {
      console.log('WhatsApp clicked');
    }
  };

  const handleMenuClick = (): void => {
    // Track mobile menu toggle
    trackEvent('mobile_menu_toggle', {
      action: isMobileMenuOpen ? 'close' : 'open'
    });
    
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = (): void => {
    setIsMobileMenuOpen(false);
    setExpandedSubmenu(null);
  };

  const toggleSubmenu = (label: string): void => {
    setExpandedSubmenu(expandedSubmenu === label ? null : label);
  };

  const handleLinkClick = (linkLabel?: string, linkUrl?: string): void => {
    // Track navigation click
    if (linkLabel && linkUrl) {
      trackMenuClick(linkLabel, 'main');
    }
    
    // Scroll to top of the page when navigating
    // Use setTimeout to ensure navigation completes first
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleMobileLinkClick = (linkLabel?: string, linkUrl?: string): void => {
    // Track mobile navigation click
    if (linkLabel && linkUrl) {
      trackMenuClick(linkLabel, 'mobile');
    }
    
    closeMobileMenu();
    // Use setTimeout to ensure menu closes before scrolling
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 150);
  };

  const handleBookingClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    // Track booking popup open
    trackEvent('booking_popup_open', {
      location: 'header',
      device_type: isMobileMenuOpen ? 'mobile' : 'desktop'
    });
    openBookingPopup();
  };

  // Locale switching helper
  const setLocale = (newLocale: 'nl' | 'ar') => {
    // Track language switch
    trackLanguageSwitch(locale, newLocale);
    
    try {
      localStorage.setItem('hm_locale', newLocale);
    } catch (_) {}
    const url = new URL(window.location.href);
    url.searchParams.set('lang', newLocale);
    window.location.href = url.toString();
  };

  // Use data from API or fallback to default
  const headerData = data as HeaderSectionData | null;
  const siteName = headerData?.siteName || 'Hairmedics';
  const gs = (globalSettings as any)?.attributes ? (globalSettings as any).attributes : (globalSettings as any);
  const headerLogoUrl = headerData?.logo?.data?.attributes?.url 
    ? getImageUrl(headerData.logo.data.attributes.url)
    : defaultLogo;
  const logoUrl = gs?.logo ? (getBestImageUrl(gs.logo) || headerLogoUrl) : headerLogoUrl;

  // Fixed NL navigation (as requested)
  const fixedNlNavigation: NavigationItem[] = [
    {
      label: 'Behandelingen',
      url: '/behandelingen',
      subpages: [{ label: 'Behandelzones', url: '/behandelzones' }],
    },
    { label: 'Tarieven', url: '/tarieven' },
    { label: 'Over ons', url: '/over-ons' },
    { label: 'Contact', url: '/contact' },
  ];

  // Fixed AR navigation
  const fixedArNavigation: NavigationItem[] = [
    {
      label: 'العلاجات',
      url: '/behandelingen',
      subpages: [{ label: 'مناطق العلاج', url: '/behandelzones' }],
    },
    { label: 'الأسعار', url: '/tarieven' },
    { label: 'من نحن', url: '/over-ons' },
    { label: 'اتصل بنا', url: '/contact' },
  ];

  // Prefer Strapi navigation for all locales; fallback to fixed per-locale
  const navigationItems: NavigationItem[] =
    headerData?.navigationItems && headerData.navigationItems.length > 0
      ? headerData.navigationItems
      : (locale === 'ar' ? fixedArNavigation : fixedNlNavigation);

  // Add subpages to Strapi data if missing
  const processedNavigationItems = navigationItems.map(item => {
    // Ensure subpage exists for NL and AR labels when missing
    const isNlTreatments = item.label === 'Behandelingen';
    const isArTreatments = item.label === 'العلاجات';
    if ((isNlTreatments || isArTreatments) && !item.subpages) {
      return {
        ...item,
        subpages: [
          { label: isArTreatments ? 'مناطق العلاج' : 'Behandelzones', url: '/behandelzones' }
        ]
      };
    }
    return item;
  });


  const contactPhone = headerData?.contactPhone || '';
  const contactEmail = headerData?.contactEmail || '';
  // Force locale-specific CTA text to avoid cross-locale bleed from Strapi
  const defaultCtaText = locale === 'ar' ? 'احجز موعداً' : 'Afspraak maken';
  const ctaUrl = headerData?.ctaButton?.url || '/contact';
  const ctaVariant = headerData?.ctaButton?.variant || 'primary';
  const ctaButton: CTAButton = { text: defaultCtaText, url: ctaUrl, variant: ctaVariant };

  const renderNavigationItem = (item: NavigationItem, index: number, isMobile = false) => {
    const isActive = location.pathname === item.url;
    const hasSubpages = Array.isArray(item.subpages) && item.subpages.length > 0;
    const isExpanded = expandedSubmenu === item.label;
    const itemClass = isMobile ? 'mobile-nav-item' : 'nav-item';
    const linkClass = isMobile ? `mobile-nav-link ${isActive ? 'active' : ''}` : `nav-link ${isActive ? 'active' : ''}`;
    

    

    
    // Mobile rendering with subpages support
    if (isMobile && hasSubpages) {
      return (
        <li key={index} className={`${itemClass} has-submenu`}>
          <Link 
            to={item.url} 
            className={linkClass}
            onClick={() => handleMobileLinkClick(item.label, item.url)}
          >
            {item.label}
          </Link>
          <ul className="mobile-submenu">
            {(item.subpages || []).map((subpage, subIndex) => (
              <li key={subIndex} className="mobile-submenu-item">
                <Link 
                  to={subpage.url} 
                  className="mobile-submenu-link"
                  onClick={() => handleMobileLinkClick(item.label, item.url)}
                >
                  {subpage.label}
                </Link>
              </li>
            ))}
          </ul>

        </li>
      );
    }

    // Desktop rendering with hover submenu
    if (!isMobile && hasSubpages) {
      return (
        <li key={index} className={`${itemClass} has-submenu desktop-submenu-parent`}>
          <Link 
            to={item.url} 
            className={linkClass}
            onClick={() => handleLinkClick(item.label, item.url)}
          >
            {item.label}
          </Link>
          <ul className="desktop-submenu">
            {(item.subpages || []).map((subpage, subIndex) => (
              <li key={subIndex} className="desktop-submenu-item">
                <Link 
                  to={subpage.url} 
                  className="desktop-submenu-link"
                  onClick={() => handleLinkClick(item.label, item.url)}
                >
                  {subpage.label}
                </Link>
              </li>
            ))}
          </ul>
        </li>
      );
    }

    // Regular mobile or desktop rendering
    if (item.isExternal || item.url.startsWith('#')) {
      return (
        <li key={index} className={itemClass}>
          <a 
            href={item.url} 
            className={linkClass} 
            target={item.isExternal ? '_blank' : '_self'}
            onClick={isMobile ? closeMobileMenu : (item.url.startsWith('#') ? handleLinkClick : undefined)}
          >
            {item.label}
          </a>
        </li>
      );
    }

    return (
      <li key={index} className={itemClass}>
        <Link 
          to={item.url} 
          className={linkClass}
          onClick={isMobile ? () => handleMobileLinkClick(item.label, item.url) : () => handleLinkClick(item.label, item.url)}
        >
          {item.label}
        </Link>
      </li>
    );
  };

  return (
    <motion.header 
      className={`header sticky-header ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <nav className="navbar">
        <div className="nav-content">
          <div className="logo">
            <Link to="/" onClick={() => handleLinkClick('Logo', '/')}>
              <img 
                src={logoUrl} 
                alt="Hairmedics Logo" 
                className="logo-img"
              />
            </Link>
          </div>
          
          <ul className="nav-menu" id="navMenu">
            {processedNavigationItems.map((item, index) => renderNavigationItem(item, index))}
            <li className="nav-item">
              <a href="#" className="nav-link" onClick={handleBookingClick}>
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width: '16px', height: '16px'}}>
                  <path d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="#000000" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15.6947 13.7002H15.7037M15.6947 16.7002H15.7037M11.9955 13.7002H12.0045M11.9955 16.7002H12.0045M8.29431 13.7002H8.30329M8.29431 16.7002H8.30329" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {ctaButton.text}
              </a>
            </li>
            {/* Locale Switcher (desktop) after CTA */}
            <li className="nav-item desktop-submenu-parent locale-switcher">
              <a className="nav-link" href="#" onClick={(e) => e.preventDefault()}>
                {locale === 'ar' ? 'AR' : 'NL'} <span className="nav-icon"><ArrowDown size={16} /></span>
              </a>
              <ul className="desktop-submenu">
                <li className="desktop-submenu-item">
                  <a href="#" className="desktop-submenu-link" onClick={(e) => { e.preventDefault(); setLocale('nl'); }}>
                    Nederlands
                  </a>
                </li>
                <li className="desktop-submenu-item">
                  <a href="#" className="desktop-submenu-link" onClick={(e) => { e.preventDefault(); setLocale('ar'); }}>
                    العربية
                  </a>
                </li>
              </ul>
            </li>
          </ul>
          
          <div className="mobile-header-actions">
            <button className="mobile-whatsapp-btn" onClick={handleWhatsAppClick}>
              <Whatsapp size={24} />
            </button>
            <button className="mobile-menu-btn" onClick={handleMenuClick}>
              <Menu size={24} />
            </button>
          </div>
          
          <button className="mobile-menu-toggle" id="mobileMenuToggle" style={{ display: 'none' }}>
            <svg className="menu-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width: '24px', height: '24px'}}>
              <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <svg className="close-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width: '24px', height: '24px', display: 'none'}}>
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            className="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeMobileMenu}
          >
            <motion.div 
              className="mobile-menu-content"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mobile-menu-header">
                <div className="mobile-menu-logo">
                  <Link to="/" onClick={() => handleMobileLinkClick('Logo', '/')}>
                    <img src={logoUrl} alt={`${siteName} - Excellence`} className="mobile-logo-img" />
                  </Link>
                </div>
                <button className="mobile-menu-close" onClick={closeMobileMenu}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              
              <nav className="mobile-nav">
                <ul className="mobile-nav-menu">
                  {processedNavigationItems.map((item, index) => renderNavigationItem(item, index, true))}
                  {/* Locale Switcher (mobile) */}
                  <li className="mobile-nav-item has-submenu">
                    <span className="mobile-nav-link">{locale === 'ar' ? 'AR' : 'NL'}</span>
                    <ul className="mobile-submenu">
                      <li className="mobile-submenu-item">
                        <a href="#" className="mobile-submenu-link" onClick={(e) => { e.preventDefault(); setLocale('nl'); }}>
                          Nederlands
                        </a>
                      </li>
                      <li className="mobile-submenu-item">
                        <a href="#" className="mobile-submenu-link" onClick={(e) => { e.preventDefault(); setLocale('ar'); }}>
                          العربية
                        </a>
                      </li>
                    </ul>
                  </li>
                </ul>
                
                <div className="mobile-cta-item">
                  <a 
                    href="#" 
                    className="mobile-nav-link mobile-cta-link"
                    onClick={handleBookingClick}
                  >
                    {ctaButton.text}
                    <ArrowRight />
                  </a>
                </div>
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

export default Header; 